import { ApiError } from 'types/ApiError';
import { WalletsService } from '../services';
import { useCallback, useState } from 'react';
import { WalletChargeFilters } from 'types/Wallet';

export const useCharges = (options?: {
	onError?: (error: ApiError) => void;
	onChargeDownload?: (chargePdfUrl: string) => void;
	onCancelChargeSuccess?: () => void;
	onBlobDownload?: (blob: Blob) => void;
}) => {
	const [isChargePdfLoading, setIsChargePdfLoading] = useState<
		string | false
	>(false);

	const [isChargeCancelLoading, setIsChargeCancelLoading] = useState<
		string | false
	>(false);

	const getChargePdf = useCallback(
		async (walletId: string, chargeId: string): Promise<string | null> => {
			try {
				setIsChargePdfLoading(chargeId);
				const chargePdf = await WalletsService.getChargePdf(
					walletId,
					chargeId,
				);
				if (typeof options?.onChargeDownload === 'function') {
					options.onChargeDownload(chargePdf.charge_pdf_url);
				}
				return chargePdf.charge_pdf_url;
			} catch (e) {
				if (typeof options?.onError === 'function') {
					options.onError(e as ApiError);
				}
				return null;
			} finally {
				setIsChargePdfLoading(false);
			}
		},
		[options],
	);

	const getChargesExportFile = useCallback(
		async (params: WalletChargeFilters): Promise<null> => {
			try {
				const res = await WalletsService.getChargeExportFile(params);
				if (typeof options?.onBlobDownload === 'function') {
					options.onBlobDownload(res);
				}
				return null;
			} catch (e) {
				if (typeof options?.onError === 'function') {
					options.onError(e as ApiError);
				}
				return null;
			}
		},
		[options],
	);

	const cancelCharge = useCallback(
		async (
			walletId: string,
			chargeId: string,
			reason: string,
		): Promise<void> => {
			try {
				setIsChargeCancelLoading(chargeId);
				await WalletsService.cancelCharge(walletId, chargeId, reason);
				options?.onCancelChargeSuccess?.();
			} catch (e) {
				if (typeof options?.onError === 'function') {
					options.onError(e as ApiError);
				}
			} finally {
				setIsChargeCancelLoading(false);
			}
		},
		[options],
	);

	return {
		isChargePdfLoading,
		isChargeCancelLoading,
		getChargePdf,
		cancelCharge,
		getChargesExportFile,
	};
};
